import { useState, useEffect, useCallback } from 'react';
import { Loader2, X, Check, UserX, Search } from 'lucide-react';
import { lmsAPI } from '../services/api';
import { BlockLoader } from './loading';

type AttendanceMark = 'attended' | 'absent' | 'excused';

interface Registration {
  id: string;
  employee_code: string;
  employee_name: string | null;
  registration_status: 'registered' | 'attended' | 'absent' | 'excused' | 'cancelled';
  registered_at: string;
  attended_at: string | null;
  check_in_at: string | null;
  assessment_score: number | null;
  assessment_passed: boolean | null;
  rating_engagement: number | null;
  rating_understanding: number | null;
  rating_application: number | null;
  points_awarded: number;
  /** Who marked or approved the row; empty for a check-in nobody has decided on. */
  marked_by?: string | null;
  /** The trainer's mark this person's check-in followed; absent from an older server. */
  marked_before_check_in?: string | null;
}

/**
 * The mark a check-in followed, for a check-in the trainer has yet to decide on;
 * null for everybody else. Marked absent or excused, the person then checked in from
 * the phone: that needs a decision of its own, so the window does not show the row as
 * present and "Mark all present" leaves it out.
 */
const contestedMark = (row: Registration): 'absent' | 'excused' | null => {
  const before = row.marked_before_check_in;
  if (before !== 'absent' && before !== 'excused') return null;
  if (row.registration_status !== 'attended' || String(row.marked_by ?? '').trim()) return null;
  return before;
};

/** The 1-5 breakdown a trainer can record behind the headline score. */
const SUB_RATINGS = [
  { key: 'rating_engagement', label: 'Engagement' },
  { key: 'rating_understanding', label: 'Understanding' },
  { key: 'rating_application', label: 'Application' },
] as const;

type SubRatingKey = (typeof SUB_RATINGS)[number]['key'];

interface LmsSessionAttendanceModalProps {
  sessionId: string;
  sessionTitle: string;
  projectId?: number;
  onClose: () => void;
  /** Called after a successful save so the list can refresh its attendance counts. */
  onSaved?: () => void;
}

/**
 * Mark who actually turned up to a training session.
 *
 * Beyond the attendance record itself this is what completes a session-shaped
 * item inside a learning journey stage, and what pays the session's points.
 */
export default function LmsSessionAttendanceModal({
  sessionId,
  sessionTitle,
  projectId,
  onClose,
  onSaved,
}: LmsSessionAttendanceModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [marks, setMarks] = useState<Record<string, AttendanceMark>>({});
  // The people the trainer has actually marked or rated in this window. Only these
  // are saved. Saving every row shown would stamp the saver's name on all of them:
  // every self check-in still waiting would count as approved, and somebody else's
  // approval or rejection would be rewritten as this person's, unseen.
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const touch = (...codes: string[]) => setTouched((prev) => new Set([...prev, ...codes]));
  // Trainer's rating per attendee, only collected when the session asks for one.
  const [scores, setScores] = useState<Record<string, string>>({});
  const [subRatings, setSubRatings] = useState<Record<string, Partial<Record<SubRatingKey, number>>>>({});
  const [hasAssessment, setHasAssessment] = useState(false);
  const [passScore, setPassScore] = useState(70);
  const [search, setSearch] = useState('');

  const fetchRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await lmsAPI.getSessionRegistrations(sessionId, projectId);
      const rows: Registration[] = response.data?.data || [];
      setRegistrations(rows);
      setHasAssessment(response.data?.meta?.has_assessment === true);
      setPassScore(response.data?.meta?.assessment_pass_score ?? 70);
      // Seed the toggles from what is already recorded, so each row shows where
      // it stands. A row the trainer does not touch is not saved at all. A check-in
      // that followed the trainer's own mark starts with nothing chosen.
      setMarks(
        rows.reduce((acc: Record<string, AttendanceMark>, row) => {
          if (contestedMark(row)) return acc;
          if (
            row.registration_status === 'attended' ||
            row.registration_status === 'absent' ||
            row.registration_status === 'excused'
          ) {
            acc[row.employee_code] = row.registration_status;
          }
          return acc;
        }, {}),
      );
      setScores(
        rows.reduce((acc: Record<string, string>, row) => {
          if (row.assessment_score !== null && row.assessment_score !== undefined) {
            acc[row.employee_code] = String(row.assessment_score);
          }
          return acc;
        }, {}),
      );
      setSubRatings(
        rows.reduce((acc: Record<string, Partial<Record<SubRatingKey, number>>>, row) => {
          const entry: Partial<Record<SubRatingKey, number>> = {};
          for (const { key } of SUB_RATINGS) {
            const value = row[key];
            if (value !== null && value !== undefined) entry[key] = value;
          }
          if (Object.keys(entry).length > 0) acc[row.employee_code] = entry;
          return acc;
        }, {}),
      );
      setError(null);
    } catch (err) {
      console.error('Error loading registrations:', err);
      setError('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  }, [sessionId, projectId]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  const handleSave = async () => {
    const attendees = Object.entries(marks).filter(([employee_code]) => touched.has(employee_code)).map(([employee_code, status]) => {
      const raw = scores[employee_code];
      const parsed = raw === undefined || raw === '' ? null : Number(raw);
      const subs = subRatings[employee_code] ?? {};
      return {
        employee_code,
        status,
        // A rating only makes sense for someone who turned up.
        assessment_score:
          hasAssessment && status === 'attended' && parsed !== null && Number.isFinite(parsed)
            ? Math.min(100, Math.max(0, Math.round(parsed)))
            : null,
        rating_engagement: status === 'attended' ? subs.rating_engagement ?? null : null,
        rating_understanding: status === 'attended' ? subs.rating_understanding ?? null : null,
        rating_application: status === 'attended' ? subs.rating_application ?? null : null,
      };
    });
    if (attendees.length === 0) {
      alert('Nothing to save. Mark somebody present, absent or excused, or enter a rating.');
      return;
    }

    try {
      setSaving(true);
      await lmsAPI.markSessionAttendance(sessionId, attendees, projectId);
      onSaved?.();
      onClose();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to save attendance';
      console.error('Error saving attendance:', err);
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const filtered = registrations.filter((r) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return (
      r.employee_code.toLowerCase().includes(query) ||
      (r.employee_name || '').toLowerCase().includes(query)
    );
  });

  const contested = registrations.filter((row) => contestedMark(row) !== null);

  // Everybody shown, except a check-in that followed the trainer's own mark: that
  // one is approved or rejected by itself, never as part of the lot.
  const markAll = (status: AttendanceMark) => {
    const covered = filtered.filter((row) => contestedMark(row) === null);
    touch(...covered.map((row) => row.employee_code));
    setMarks(
      covered.reduce(
        (acc: Record<string, AttendanceMark>, row) => {
          acc[row.employee_code] = status;
          return acc;
        },
        { ...marks },
      ),
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Session Attendance</h2>
            <p className="text-sm text-gray-500">{sessionTitle}</p>
            {hasAssessment && (
              <p className="mt-1 text-xs text-amber-700">
                This session collects a trainer rating — pass mark {passScore}%.
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4">
          {loading ? (
            <BlockLoader label="Loading registrations…" className="py-10" />
          ) : error ? (
            <p className="py-10 text-center text-sm text-gray-500">{error}</p>
          ) : registrations.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">
              Nobody has registered for this session yet.
            </p>
          ) : (
            <>
              <div className="mb-3 flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search attendees…"
                    className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <button
                  onClick={() => markAll('attended')}
                  className="whitespace-nowrap rounded-lg border border-green-200 px-3 py-2 text-xs font-medium text-green-700 hover:bg-green-50"
                >
                  Mark all present
                </button>
              </div>

              {contested.length > 0 && (
                <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {contested.length === 1
                    ? '1 learner checked in after being marked absent or excused.'
                    : `${contested.length} learners checked in after being marked absent or excused.`}
                  {' '}"Mark all present" leaves {contested.length === 1 ? 'that one' : 'those'} out: choose Present or Absent on {contested.length === 1 ? 'that row' : 'each row'}.
                </p>
              )}

              <div className="max-h-80 space-y-2 overflow-y-auto">
                {filtered.map((row) => {
                  const mark = marks[row.employee_code];
                  const before = contestedMark(row);
                  return (
                    <div
                      key={row.id}
                      className="rounded-lg border border-gray-200 px-3 py-2"
                    >
                      <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {row.employee_name || row.employee_code}
                        </p>
                        <p className="text-xs text-gray-500">
                          {row.employee_code}
                          {row.check_in_at && ' · self check-in'}
                          {before && (
                            <span className="text-amber-700">
                              {before === 'excused' ? ' · after being excused' : ' · after being marked absent'} · awaiting your decision
                            </span>
                          )}
                          {row.points_awarded > 0 && ` · ${row.points_awarded} pts awarded`}
                          {row.assessment_score !== null && row.assessment_score !== undefined && (
                            ` · rated ${row.assessment_score}%${row.assessment_passed === false ? ' (fail)' : ''}`
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {hasAssessment && mark === 'attended' && (
                          <input
                            type="number"
                            value={scores[row.employee_code] ?? ''}
                            onChange={(e) => {
                              touch(row.employee_code);
                              setScores({ ...scores, [row.employee_code]: e.target.value });
                            }}
                            className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Rating"
                            min={0}
                            max={100}
                            title={`Trainer rating (%) — pass mark is ${passScore}%`}
                          />
                        )}
                        <button
                          onClick={() => {
                            touch(row.employee_code);
                            setMarks({ ...marks, [row.employee_code]: 'attended' });
                          }}
                          className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors ${
                            mark === 'attended'
                              ? 'bg-green-100 text-green-700'
                              : 'text-gray-500 hover:bg-gray-100'
                          }`}
                        >
                          <Check className="h-3.5 w-3.5" />
                          Present
                        </button>
                        <button
                          onClick={() => {
                            touch(row.employee_code);
                            setMarks({ ...marks, [row.employee_code]: 'absent' });
                          }}
                          className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors ${
                            mark === 'absent' ? 'bg-red-100 text-red-700' : 'text-gray-500 hover:bg-gray-100'
                          }`}
                        >
                          <UserX className="h-3.5 w-3.5" />
                          Absent
                        </button>
                        <button
                          onClick={() => {
                            touch(row.employee_code);
                            setMarks({ ...marks, [row.employee_code]: 'excused' });
                          }}
                          className={`rounded-lg px-2 py-1 text-xs font-medium transition-colors ${
                            mark === 'excused' ? 'bg-amber-100 text-amber-700' : 'text-gray-500 hover:bg-gray-100'
                          }`}
                          title="Approved non-attendance"
                        >
                          Excused
                        </button>
                      </div>
                      </div>

                      {/* Optional 1-5 breakdown behind the headline score. */}
                      {hasAssessment && mark === 'attended' && (
                        <div className="mt-2 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-2">
                          {SUB_RATINGS.map(({ key, label }) => (
                            <label key={key} className="flex items-center gap-1 text-xs text-gray-600">
                              {label}
                              <select
                                value={subRatings[row.employee_code]?.[key] ?? ''}
                                onChange={(e) => {
                                  touch(row.employee_code);
                                  setSubRatings({
                                    ...subRatings,
                                    [row.employee_code]: {
                                      ...(subRatings[row.employee_code] ?? {}),
                                      [key]: e.target.value ? Number(e.target.value) : undefined,
                                    },
                                  });
                                }}
                                className="rounded border border-gray-300 px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                              >
                                <option value="">–</option>
                                {[1, 2, 3, 4, 5].map((n) => (
                                  <option key={n} value={n}>{n}</option>
                                ))}
                              </select>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading || registrations.length === 0}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? 'Saving…' : 'Save Attendance'}
          </button>
        </div>
      </div>
    </div>
  );
}
